import { useEffect, useState } from "react";
import { registerPhoneWebhook } from "../apis/whatsappAccount";

const EMBEDDED_SIGNUP_CONFIG_ID = "1404119364652323";
const REDIRECT_URI = "https://pingfront.ngrok-free.app/whatsapp/callback";

const WhatsappBusiness = () => {
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [reVerifyingPhoneId, setReVerifyingPhoneId] = useState(null);

  // Fetch WhatsApp accounts
  const fetchWhatsappAccounts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("userAccessToken");
      if (!token) return;

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/whatsapp-accounts`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      if (data.success) {
        setAccounts(data.data || []);
        if (data.data?.length > 0) setConnected(true);
      } else {
        setError(data.error || "Failed to load WhatsApp accounts");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Server error while fetching WhatsApp accounts");
    } finally {
      setLoading(false);
    }
  };

  // Re-verify phone number and register webhook
  const handleReVerifyPhone = async (phoneId) => {
    try {
      setReVerifyingPhoneId(phoneId);
      const token = localStorage.getItem("userAccessToken");
      if (!token) {
        setError("⚠️ Authentication token not found. Please log in again.");
        return;
      }

      const response = await registerPhoneWebhook(token, phoneId);
      if (response.data.success) {
        setMessage(
          "✅ Phone number re-verified and webhook registered successfully!"
        );
        setError("");
        fetchWhatsappAccounts();
      } else {
        setError(response.data.message || "Failed to re-verify phone number.");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Server error while re-verifying phone number. Please try again."
      );
    } finally {
      setReVerifyingPhoneId(null);
    }
  };

  // Delete WhatsApp Phone Number
  const handleDeletePhoneNumber = async (phone_id) => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect this WhatsApp number?"
      )
    )
      return;

    try {
      const token = localStorage.getItem("userAccessToken");
      if (!token) return;

      setLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/whatsapp-accounts/${phone_id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();

      if (data.success) {
        setMessage("✅ WhatsApp number disconnected successfully!");
        fetchWhatsappAccounts();
      } else {
        setError(data.message || "Something went wrong.");
      }
    } catch (err) {
      console.error("❌ Delete error:", err);
      setError("Server error while disconnecting phone number.");
    } finally {
      setLoading(false);
    }
  };

  // Send code to backend
  const sendCodeToBackend = async (code) => {
    const token = localStorage.getItem("userAccessToken");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/facebook/embedded-signup/callback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ code }),
          credentials: "include",
        }
      );
      const data = await res.json();
      setMessage("🎉 WhatsApp onboarding successful!");
      setError("");
      fetchWhatsappAccounts();
    } catch (error) {
      console.error("Failed to send signup code to backend:", error);
      setError("Failed to complete onboarding. Please try again.");
    }
  };

  // Handle Embedded Signup
  const handleEmbeddedSignup = () => {
    setLoading(true);
    setError("");
    setMessage("");

    const token = localStorage.getItem("userAccessToken");
    if (!token) {
      setError("⚠️ Please log in to your account first.");
      setLoading(false);
      return;
    }

    if (window.FB && typeof window.FB.login === "function") {
      window.FB.login(
        (response) => {
          setLoading(false);
          if (response.authResponse && response.authResponse.code) {
            const code = response.authResponse.code;
            sendCodeToBackend(code);
          } else {
            console.warn(
              "No auth code received in FB login response",
              response
            );
            setError("Failed to get authorization code. Please try again.");
          }
        },
        {
          config_id: EMBEDDED_SIGNUP_CONFIG_ID,
          response_type: "code",
          override_default_response_type: true,
          redirect_uri: REDIRECT_URI,
        }
      );
    } else {
      setLoading(false);
      setError("Facebook SDK not loaded. Please refresh and try again.");
    }
  };

  const handleRetry = () => {
    setError("");
    setMessage("");
    setLoading(false);
    fetchWhatsappAccounts();
  };

  // Check if phone has verification or webhook issues
  const hasVerificationIssue = (account) => {
    return (
      account.code_verification_status === "EXPIRED" ||
      account.is_webhook_registered === 0
    );
  };

  // Get appropriate error message
  const getErrorMessage = (account) => {
    if (account.code_verification_status === "EXPIRED") {
      return "Verification code has expired";
    }
    if (account.is_webhook_registered === 0) {
      return `Webhook registration failed: ${
        account.webhook_registration_error || "Unknown error"
      }`;
    }
    return "";
  };

  useEffect(() => {
    fetchWhatsappAccounts();
  }, []);

  return (
    <div className="min-h-screen bg-[#ffff] p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                WhatsApp Business
              </h1>
              <p className="text-sm text-gray-600">
                Manage your WhatsApp Business accounts
              </p>
            </div>
            {connected && accounts.length > 0 && (
              <button
                onClick={handleEmbeddedSignup}
                disabled={loading}
                className="inline-flex items-center gap-2 text-gray-900 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Number
              </button>
            )}
          </div>

          {/* Stats Cards */}
          {connected && accounts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div
                className="rounded-2xl p-5 border border-gray-200 hover:shadow-sm transition-all"
                style={{ backgroundColor: "#e9eeea" }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-2 font-medium uppercase tracking-wide">
                      Total Accounts
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {new Set(accounts.map((a) => a.waba_id || a.id)).size}
                    </p>
                  </div>
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: "#eefa94" }}
                  >
                    <svg
                      className="w-7 h-7 text-gray-900"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div
                className="rounded-2xl p-5 border border-gray-200 hover:shadow-sm transition-all"
                style={{ backgroundColor: "#eefa94" }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-800 mb-2 font-medium uppercase tracking-wide">
                      Phone Numbers
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {accounts.length}
                    </p>
                  </div>
                  <div className="w-14 h-14 bg-white/60 rounded-2xl flex items-center justify-center border border-gray-200">
                    <svg
                      className="w-7 h-7 text-gray-900"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div
                className="rounded-2xl p-5 border border-gray-200 hover:shadow-sm transition-all"
                style={{ backgroundColor: "#e9eeea" }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-2 font-medium uppercase tracking-wide">
                      Active Numbers
                    </p>
                    <p className="text-3xl font-bold text-emerald-600">
                      {accounts.filter((a) => !hasVerificationIssue(a)).length}
                    </p>
                  </div>
                  <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center border border-emerald-200">
                    <svg
                      className="w-7 h-7 text-emerald-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-800 font-medium">{error}</p>
                <button
                  onClick={handleRetry}
                  className="mt-2 text-sm text-red-700 hover:text-red-900 font-medium underline"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {message && !error && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm text-emerald-800 font-medium">{message}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="animate-spin h-16 w-16 border-4 border-gray-300 border-t-gray-900 rounded-full"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <svg
                  className="w-6 h-6 text-gray-900"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
            </div>
            <p className="mt-4 text-gray-700 font-medium">Loading accounts...</p>
          </div>
        )}

        {/* Empty State */}
        {!connected && !loading && !error && accounts.length === 0 && (
          <div className="max-w-lg mx-auto">
            <div
              className="rounded-3xl border border-gray-200 p-12 text-center"
              style={{ backgroundColor: "#e9eeea" }}
            >
              <div
                className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6 border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                <svg
                  className="w-10 h-10 text-gray-900"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Connect Your WhatsApp Business
              </h2>
              <p className="text-gray-600 mb-8">
                Link your Facebook account to start managing WhatsApp Business
                numbers and reach your customers instantly.
              </p>
              <button
                onClick={handleEmbeddedSignup}
                disabled={loading}
                className="inline-flex items-center gap-3 text-gray-900 px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Connect with Facebook
              </button>
            </div>
          </div>
        )}

        {/* Phone Numbers Grid */}
        {connected && accounts.length > 0 && !loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {accounts.map((account) => (
              <div
                key={account.phone_id || account.id}
                className="rounded-2xl border border-gray-200 hover:shadow-md transition-all duration-200 overflow-hidden group"
                style={{ backgroundColor: "#e9eeea" }}
              >
                {/* Card Header */}
                <div className="px-5 py-4 border-b border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-gray-900 truncate mb-1">
                        {account.phone_name ||
                          account.verified_name ||
                          "Unnamed Account"}
                      </h3>
                      <p className="text-sm text-gray-600 truncate">
                        {account.account_name || "Business Account"}
                      </p>
                    </div>
                    <div className="flex-shrink-0 ml-2">
                      {hasVerificationIssue(account) ? (
                         <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300">

                           <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></span>
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300">
                          <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></span>
                          Active
                        </span>
                      )}
                    </div>
                  </div>

                  {/* WhatsApp Icon */}
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform border border-gray-300"
                    style={{ backgroundColor: "#fff" }}
                  >
                    <svg
                      className="w-7 h-7 text-gray-900"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5 space-y-4">
                  {/* Phone Number */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">
                      Phone Number
                    </label>
                    <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-2.5 border border-gray-200">
                      <svg
                        className="w-4 h-4 text-gray-500 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <span className="text-sm font-semibold text-gray-900 break-all">
                        {account.phone_number}
                      </span>
                    </div>
                  </div>

                  {/* WABA ID */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">
                      WABA ID
                    </label>
                    <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-2.5 border border-gray-200">
                      <svg
                        className="w-4 h-4 text-gray-500 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                        />
                      </svg>
                      <span className="text-xs font-mono text-gray-700 break-all font-medium">
                        {account.waba_id || account.id || "N/A"}
                      </span>
                    </div>
                  </div>

                  {/* Issue Alert */}
                  {/* {hasVerificationIssue(account) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <div className="flex items-start gap-2 mb-3">
                        <svg
                          className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <p className="text-xs text-amber-800 font-medium">
                          {getErrorMessage(account)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleReVerifyPhone(account.phone_id)}
                        disabled={reVerifyingPhoneId === account.phone_id}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-2 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {reVerifyingPhoneId === account.phone_id
                          ? "Processing..."
                          : "Re-verify Number"}
                      </button>
                    </div>
                  )} */}

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDeletePhoneNumber(account.phone_id)}
                    className="w-full bg-red-50 hover:bg-red-100 text-red-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-all border border-red-200 hover:border-red-300 flex items-center justify-center gap-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Disconnect Number
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsappBusiness;
